/**
 * Activity Item Component - Used in CompanyProfilePage
 */
import { useState } from "react";
import { Phone, Calendar, FileText, Award, User, Clock, ChevronRight, Trash2, Mail, MessageCircle } from "lucide-react";
import { formatDateTime, formatDate } from "../../utils/dateTime";

// Activity Types Config
const ACTIVITY_TYPES = [
  { value: "INITIAL_CONTACT", label: "Inicijalni kontakt", icon: MessageCircle, color: "indigo" },
  { value: "POZIV", label: "Poziv", icon: Phone, color: "blue" },
  { value: "DOGOVOREN_SASTANAK", label: "Dogovoren sastanak", icon: Calendar, color: "green" },
  { value: "PONUDA_POSLANA", label: "Ponuda", icon: FileText, color: "amber" },
  { value: "AKVIZICIJA", label: "Akvizicija", icon: Award, color: "rose" },
];

const colorClasses = {
  blue: "bg-blue-100 text-[#1B3F8F]",
  green: "bg-green-100 text-green-600",
  amber: "bg-amber-100 text-amber-600",
  rose: "bg-rose-100 text-rose-600",
  indigo: "bg-indigo-100 text-indigo-600",
};

const EMAIL_OUTCOME_LABELS = {
  POSITIVE_REPLY: { label: "Pozitivan odgovor", color: "bg-green-100 text-green-700" },
  CALL_REQUESTED: { label: "Traži poziv", color: "bg-blue-100 text-blue-700" },
  MEETING_REQUESTED: { label: "Zatražen sastanak", color: "bg-emerald-100 text-emerald-700" },
  NO_REPLY: { label: "Bez odgovora", color: "bg-gray-100 text-gray-600" },
  NEGATIVE_REPLY: { label: "Negativan odgovor", color: "bg-red-100 text-red-700" },
  DO_NOT_CONTACT: { label: "Ne kontaktirati", color: "bg-red-100 text-red-700" },
  BOUNCE: { label: "E-mail nevažeći (bounce)", color: "bg-orange-100 text-orange-700" },
};

const getOutcomeLabel = (outcome) => {
  const labels = {
    NO_ANSWER: "Nije se javio",
    CALL_BACK: "Nazvati ponovo",
    MEETING_SCHEDULED: "Dogovoren sastanak",
    NOT_INTERESTED: "Nije zainteresiran",
    DO_NOT_CONTACT: "Ne kontaktirati"
  };
  return labels[outcome] || outcome;
};

const getOutcomeColor = (outcome) => {
  const colors = {
    NO_ANSWER: "text-gray-500",
    CALL_BACK: "text-amber-600",
    MEETING_SCHEDULED: "text-green-600",
    NOT_INTERESTED: "text-red-500",
    DO_NOT_CONTACT: "text-red-600"
  };
  return colors[outcome] || "text-gray-500";
};

const getMeetingOutcomeLabel = (outcome) => {
  const labels = { HELD: "Održan", NO_SHOW: "Nije se pojavio", RESCHEDULED: "Odgođen", LOST: "Izgubljen" };
  return labels[outcome] || outcome;
};

const getMeetingOutcomeColor = (outcome) => {
  const colors = {
    HELD: "bg-green-100 text-green-700",
    NO_SHOW: "bg-amber-100 text-amber-700",
    RESCHEDULED: "bg-blue-100 text-blue-700",
    LOST: "bg-red-100 text-red-700"
  };
  return colors[outcome] || "bg-gray-100 text-gray-700";
};

const getNextStepLabel = (step) => {
  const labels = { FOLLOW_UP_CALL: "Follow-up poziv", REQUEST_OFFER_BACKOFFICE: "Zatražiti ponudu", NEW_MEETING: "Novi sastanak" };
  return labels[step] || step;
};

export const ActivityItem = ({ activity, onOutcomeClick, onDeleteClick, onEmailOutcomeClick, isAdmin }) => {
  const [noteExpanded, setNoteExpanded] = useState(false);
  const typeInfo = ACTIVITY_TYPES.find(t => t.value === activity.type) || ACTIVITY_TYPES[0];
  const Icon = typeInfo.icon;
  const needsOutcome = activity.type === "DOGOVOREN_SASTANAK" && !activity.meeting_outcome;
  const isEmailWaiting = activity.type === "INITIAL_CONTACT" && activity.channel === "EMAIL" && !activity.email_outcome;
  const emailOutcomeMeta = activity.email_outcome ? EMAIL_OUTCOME_LABELS[activity.email_outcome] : null;

  return (
    <div className="flex gap-3 py-3 border-b border-gray-50 last:border-0" data-testid={`activity-item-${activity.id}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClasses[typeInfo.color]}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-gray-900 text-sm">{typeInfo.label}</p>
            {activity.type === "POZIV" && activity.outcome && (
              <span className={`text-xs px-2 py-0.5 rounded-full bg-gray-100 ${getOutcomeColor(activity.outcome)}`}>
                {getOutcomeLabel(activity.outcome)}
              </span>
            )}
            {activity.type === "DOGOVOREN_SASTANAK" && activity.meeting_outcome && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${getMeetingOutcomeColor(activity.meeting_outcome)}`}>
                {getMeetingOutcomeLabel(activity.meeting_outcome)}
              </span>
            )}
            {activity.type === "INITIAL_CONTACT" && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 flex items-center gap-1">
                {activity.channel === "EMAIL" ? <Mail size={11} /> : <Phone size={11} />}
                {activity.channel === "EMAIL" ? "E-mail" : "Poziv"}
              </span>
            )}
            {isEmailWaiting && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                Čeka se odgovor
              </span>
            )}
            {emailOutcomeMeta && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${emailOutcomeMeta.color}`}>
                {emailOutcomeMeta.label}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 flex-shrink-0">{formatDate(activity.created_at)}</p>
        </div>
        
        {(activity.type === "DOGOVOREN_SASTANAK" || activity.outcome === "MEETING_SCHEDULED") && activity.contact_name && (
          <div className="mt-1 text-xs text-gray-600 bg-green-50 rounded-lg px-2 py-1.5 inline-flex items-center gap-2">
            <User size={12} className="text-green-600" />
            <span>{activity.contact_name}</span>
            {activity.contact_phone && (
              <>
                <span className="text-gray-300">•</span>
                <Phone size={12} className="text-green-600" />
                <span>{activity.contact_phone}</span>
              </>
            )}
          </div>
        )}

        {activity.meeting_datetime && !activity.rescheduled_datetime && (
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1 font-medium">
            <Calendar size={12} />
            Sastanak: {formatDateTime(activity.meeting_datetime)}
          </p>
        )}

        {activity.rescheduled_datetime && (
          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1 font-medium">
            <Calendar size={12} />
            {activity.meeting_outcome === "RESCHEDULED" ? "Novi termin:" : "Sljedeći sastanak:"} {formatDateTime(activity.rescheduled_datetime)}
          </p>
        )}

        {activity.meeting_outcome === "HELD" && activity.next_step && (
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
            <ChevronRight size={12} />
            Sljedeći korak: {getNextStepLabel(activity.next_step)}
          </p>
        )}

        {activity.meeting_outcome === "LOST" && activity.lost_reason && (
          <p className="text-xs text-red-500 mt-1">Razlog: {activity.lost_reason}</p>
        )}

        {activity.type === "AKVIZICIJA" && activity.estimated_value && (
          <div className="mt-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg px-3 py-2 inline-flex items-center gap-2">
            <span className="text-purple-500">💰</span>
            <span>Vrijednost: {activity.estimated_value.toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KM</span>
          </div>
        )}

        {activity.note && (
          <div className="mt-1">
            <p className={`text-sm text-gray-500 cursor-pointer hover:text-gray-700 ${noteExpanded ? '' : 'line-clamp-2'}`} onClick={() => setNoteExpanded(!noteExpanded)}>
              {activity.note}
            </p>
            {!noteExpanded && activity.note.length > 60 && (
              <button onClick={() => setNoteExpanded(true)} className="text-[#1B3F8F] text-xs hover:underline mt-0.5">Prikaži više ▼</button>
            )}
            {noteExpanded && (
              <button onClick={() => setNoteExpanded(false)} className="text-[#1B3F8F] text-xs hover:underline mt-0.5">Prikaži manje ▲</button>
            )}
          </div>
        )}
        
        {activity.next_action_date && !activity.meeting_datetime && !activity.rescheduled_datetime && (
          <p className="text-xs text-[#1B3F8F] mt-1 flex items-center gap-1">
            <Calendar size={12} />
            Sljedeća akcija: {formatDateTime(activity.next_action_date)}
          </p>
        )}
        
        {activity.user_name && <p className="text-xs text-gray-400 mt-1">{activity.user_name}</p>}

        <div className="flex items-center gap-2 mt-2">
          {needsOutcome && onOutcomeClick && (
            <button onClick={() => onOutcomeClick(activity)} data-testid={`meeting-outcome-btn-${activity.id}`} className="px-3 py-1.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors flex items-center gap-1">
              <Clock size={12} />
              Unesi ishod
            </button>
          )}
          {isEmailWaiting && onEmailOutcomeClick && (
            <button
              onClick={() => onEmailOutcomeClick(activity)}
              data-testid={`email-outcome-btn-${activity.id}`}
              className="px-3 py-1.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center gap-1"
            >
              <Mail size={12} />
              Unesi ishod e-maila
            </button>
          )}
          {isAdmin && onDeleteClick && (
            <button onClick={() => onDeleteClick(activity)} data-testid={`delete-activity-btn-${activity.id}`} className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1">
              <Trash2 size={12} />
              Obriši
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityItem;
